import React, { useContext, useEffect, useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Spin, Checkbox, Divider } from 'antd';
import { SaveOutlined } from '@ant-design/icons';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';

const { Title } = Typography;

function Settings({ history }) {
  const { setHeaderComponent } = useContext(LayoutContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);

  const { doFetch: fetchSettings } = useFetch();
  const { doFetch: updateSettings } = useFetch();

  const loadSettings = () => {
    setLoading(true);
    fetchSettings({
      url: URLS.userSettings,
      onSuccess: (data) => {
        form.setFieldsValue({
          business_api_url: data.business_api_url || '',
          business_frontend_url: data.business_frontend_url || '',
          is_showing_types: data.is_showing_types !== false,
          is_showing_colors: data.is_showing_colors !== false,
          is_showing_sizes: data.is_showing_sizes !== false,
          is_showing_flavors: data.is_showing_flavors === true,
          is_showing_subcategories: data.is_showing_subcategories !== false,
          is_showing_brands: data.is_showing_brands !== false,
          is_showing_additional_images: data.is_showing_additional_images !== false,
        });
        setLoading(false);
      },
      onError: () => {
        message.error('Failed to load settings');
        setLoading(false);
      },
    });
  };

  const handleSubmit = (values) => {
    updateSettings({
      url: URLS.userSettings,
      method: 'PUT',
      params: values,
      onSuccess: () => {
        message.success('Settings updated successfully');
      },
      onError: () => {
        message.error('Failed to update settings');
      },
    });
  };

  useEffect(() => {
    loadSettings();

    // Clear any existing header component when this page loads
    setHeaderComponent(null);

    return () => {
      setHeaderComponent(null);
    };
  }, []);

  return (
    <div className="settings-page-container" style={{ padding: '24px' }}>
      <Card
        title={
          <Title level={2} style={{ margin: 0 }}>
            User Settings
          </Title>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              label="Business API URL"
              name="business_api_url"
              rules={[
                {
                  type: 'url',
                  message: 'Please enter a valid URL',
                },
                {
                  pattern: /^https?:\/\/.+/,
                  message: 'URL must start with http:// or https://',
                },
              ]}
              extra="The base URL for your business API (e.g., https://api.yourbusiness.com)"
            >
              <Input
                placeholder="https://api.yourbusiness.com"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Business Frontend URL"
              name="business_frontend_url"
              rules={[
                {
                  type: 'url',
                  message: 'Please enter a valid URL',
                },
                {
                  pattern: /^https?:\/\/.+/,
                  message: 'URL must start with http:// or https://',
                },
              ]}
              extra="The base URL for your business frontend (e.g., https://yourbusiness.com)"
            >
              <Input
                placeholder="https://yourbusiness.com"
                size="large"
              />
            </Form.Item>

            <Divider>Display Settings</Divider>

            <Form.Item
              label="Show Product Types"
              name="is_showing_types"
              valuePropName="checked"
              extra="Enable/disable display of product types in the menu"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Show Product Colors"
              name="is_showing_colors"
              valuePropName="checked"
              extra="Enable/disable display of product colors in the menu"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Show Product Sizes"
              name="is_showing_sizes"
              valuePropName="checked"
              extra="Enable/disable display of product sizes in the menu"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Show Product Flavors"
              name="is_showing_flavors"
              valuePropName="checked"
              extra="Enable/disable display of product flavors in the product form"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Show Subcategories"
              name="is_showing_subcategories"
              valuePropName="checked"
              extra="Enable/disable display of subcategories in the menu"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Show Brands"
              name="is_showing_brands"
              valuePropName="checked"
              extra="Enable/disable display of brands in the menu"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Show Additional Images"
              name="is_showing_additional_images"
              valuePropName="checked"
              extra="Enable/disable display of additional product images"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                size="large"
                loading={updateSettings.loading}
              >
                Save Settings
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
}

export default Settings;
